import { FormControl, MenuItem, Select, Tooltip } from '@mui/material';
import { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import moment from 'moment';
import PropTypes from 'prop-types';

import { AvatarImage } from '../../components/Avatar';
import { DELETE_WORKSPACE_INVITE, DELETE_WORKSPACE_PROFILE, UPDATE_WORKSPACE_INVITE } from '../../apis/workspace';
import { REASSIGN_PROJECT } from '../../apis/project';
import { RESEND_INVITE_ALLOWED_IN_MINUTES } from '../../constants/workspaces';
import Button from '../../components/atoms/button';
import Modal from '../../components/organisms/modal';
import useWorkspace from '../../context/WorkspaceContext';

const TooltipContentStyles = {
  tooltip: {
    sx: {
      color: '#FFFFFF',
      background: '#344054',
      fontWeight: '500',
      fontSize: '12px',
      padding: '12px',
      lineHeight: '18px',
      borderRadius: '6px',
      marginTop: '9px !important',
    },
  },
  arrow: {
    sx: {
      color: '#344054',
      marginRight: '50%',
    },
  },
};

const UserActions = (props) => {
  // init
  const { workspaceProfile } = useWorkspace();
  const { data, setToast, api, node, setRefreshTable } = props;

  // states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [projectAvailable, setProjectAvailable] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [open, setOpen] = useState(false);
  const [usersWithProjects, setUsersWithProjects] = useState([]);

  // mutations/queries
  const [deleteProfile, { loading: deleteProfileLoading }] = useMutation(DELETE_WORKSPACE_PROFILE);
  const [reassignProject, { loading: reassignProjectLoading }] = useMutation(REASSIGN_PROJECT);

  const handleDeleteUser = async () => {
    try {
      await deleteProfile({ variables: { input: { id: data.id } } });
      api.applyTransaction({ remove: [{ id: node.data.id }] });
      setToast((prevstate) => ({ ...prevstate, message: 'User deleted successfully!', open: true, type: 'success' }));
    } catch (error) {
      setToast((prevstate) => ({ ...prevstate, message: 'Something went wrong while deleting user!', open: true, type: 'error' }));
    } finally {
      if (!deleteProfileLoading) {
        setIsModalOpen(false);
      }
    }
  };

  const handleReassignProject = async () => {
    try {
      await reassignProject({
        variables: {
          input: {
            deleteWorkspaceProfileId: data.alldata.id,
            assignWorkspaceProfileId: selectedUserId,
          },
        },
      });
      setToast((prevstate) => ({ ...prevstate, message: 'Projects reassigned successfully!', open: true, type: 'success' }));
      setRefreshTable((prev) => !prev);
    } catch (error) {
      setToast((prevstate) => ({ ...prevstate, message: 'Something went wrong while reassigning projects!', open: true, type: 'error' }));
    } finally {
      if (!reassignProjectLoading) {
        setIsModalOpen(false);
      }
    }
  };

  const handleModalStatus = () => {
    if (data.projects.length > 0) {
      const users = [];
      api.forEachNode((rowNode) => {
        const user = rowNode.data.alldata;
        if (
          user.id !== data.alldata.id &&
          (user.roleId === 1 ||
            user.roleId === 2)
        ) {
          users.push({
            id: user.id,
            displayName: user.displayName,
            email: rowNode.data.emailAddress,
            logo: user.logo,
          });
        }
      });
      setUsersWithProjects(users);
      setProjectAvailable(false);
    }
    setIsModalOpen(!isModalOpen);
  };

  const isDeleteUserAllowed = useMemo(() => {
    if (workspaceProfile.id === data.id) {
      return false;
    }

    if (data.alldata.roleId === 1 && workspaceProfile.roleId !== 1) {
      return false;
    }

    return true;
  }, []);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleChange = (event) => {
    setSelectedUserId(event.target.value);
  };

  return (
    <div className="user-table-actions">
      <Button disabled={!isDeleteUserAllowed} onClick={handleModalStatus} primary outlined small>Delete User</Button>
      {projectAvailable ? (
        <Modal
          title="Delete User"
          body="Are you sure you want to delete this user?"
          secondaryButton={(<Button isLoading={deleteProfileLoading} disabled={deleteProfileLoading} onClick={handleDeleteUser} danger>Delete User</Button>)}
          primaryButton={(<Button disabled={deleteProfileLoading} primary outlined onClick={handleModalStatus}>Cancel</Button>)}
          isOpen={isModalOpen}
          actionAlignment="right"
          setIsOpen={setIsModalOpen}
        />
      ) : (
        <Modal
          title="Delete and reassign project to other user"
          body={(
            <div>
              <p>Are you sure you want to delete user <b> {data.alldata.displayName} </b>and reassign their projects to:</p>
              <FormControl fullWidth>
                {usersWithProjects.length > 0 && (
                  <Select
                    IconComponent={ExpandMoreIcon}
                    open={open}
                    onClose={handleClose}
                    onOpen={handleOpen}
                    displayEmpty
                    value={selectedUserId || ''}
                    defaultValue=""
                    onChange={handleChange}
                    inputProps={{ 'aria-label': 'Without label' }}
                  >
                    <MenuItem disabled value="">
                      Select a User
                    </MenuItem>
                    {usersWithProjects.map((user) => (
                      <MenuItem key={user.id} value={user.id} className="select-item">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <AvatarImage borderGray name={user.displayName} alt={user.displayName} fontSize={15} height={30} width={30} initials={2} src={user.logo} />
                          <span>{user.email}</span>
                        </div>
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </FormControl>
            </div>
          )}
          secondaryButton={(<Button isLoading={reassignProjectLoading} disabled={reassignProjectLoading} onClick={handleReassignProject} danger>Delete User</Button>)}
          primaryButton={(<Button disabled={reassignProjectLoading} primary outlined onClick={handleModalStatus}>Cancel</Button>)}
          isOpen={isModalOpen}
          actionAlignment="right"
          setIsOpen={setIsModalOpen}
        />
      )}
    </div>
  );
};

const InviteActions = (props) => {
  // init
  const { data, setToast, api, node } = props;

  // constants
  const lastUpdatedTime = useMemo(() => (moment(parseInt(data.alldata.updatedAt, 10))), []);
  const remainingTime = useMemo(() => (moment.duration(moment(lastUpdatedTime).add(RESEND_INVITE_ALLOWED_IN_MINUTES / 60, 'hour').diff(moment())).humanize()), []);

  // states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isResendAvailable, setIsResendAvailable] = useState(Math.abs(moment().diff(moment(lastUpdatedTime), 'minutes')) > RESEND_INVITE_ALLOWED_IN_MINUTES);

  // mutations/queries
  const [deleteWorkspaceInvite, { loading: deleteLoading }] = useMutation(DELETE_WORKSPACE_INVITE);
  const [updateWorkspaceInvite, { loading: resendLoading }] = useMutation(UPDATE_WORKSPACE_INVITE);

  const handleModalStatus = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleDeleteInvite = async () => {
    try {
      await deleteWorkspaceInvite({ variables: { input: { id: data.id } } });
      api.applyTransaction({ remove: [{ id: node.data.id }] });
      setToast((prevstate) => ({ ...prevstate, message: 'Invite deleted successfully!', open: true, type: 'success' }));
    } catch (error) {
      setToast((prevstate) => ({ ...prevstate, message: 'Something went wrong while deleting invite!', open: true, type: 'error' }));
    } finally {
      if (!deleteLoading) {
        setIsModalOpen(false);
      }
    }
  };

  const handleResendInvite = async () => {
    try {
      await updateWorkspaceInvite({ variables: { input: { id: data.id, role: data.role } } });
      const rowData = api.getRowNode(node.id);
      rowData.setDataValue('status', moment().fromNow());
      setIsResendAvailable(false);
      setToast((prevstate) => ({ ...prevstate, message: 'Invite resent successfully!', open: true, type: 'success' }));
    } catch (error) {
      setToast((prevstate) => ({ ...prevstate, message: 'Something went wrong while resending invite!', open: true, type: 'error' }));
    }
  };

  return (
    <div className="user-table-actions">
      <Tooltip
        open={showTooltip && !isResendAvailable}
        onPointerEnter={() => setShowTooltip(true)}
        onPointerLeave={() => setShowTooltip(false)}
        key="tooltip"
        componentsProps={TooltipContentStyles}
        title={`Resend available after ${remainingTime}`}
        arrow
      >
        <span>
          <Button onClick={handleResendInvite} isLoading={resendLoading} disabled={!isResendAvailable || resendLoading} primary small>Resend</Button>
        </span>
      </Tooltip>
      <Button onClick={handleModalStatus} primary outlined small>Delete</Button>
      <Modal
        title="Delete Invite"
        body="Are you sure you want to delete this Invite?"
        secondaryButton={(<Button isLoading={deleteLoading} disabled={deleteLoading} onClick={handleDeleteInvite} danger>Delete Invite</Button>)}
        primaryButton={(<Button disabled={deleteLoading} primary outlined onClick={handleModalStatus}>Cancel</Button>)}
        actionAlignment="right"
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
      />
    </div>
  );
};

const UserTableActions = (props) => {
  const { data, setRefreshTable } = props;

  if (data.type === 'user') {
    return <UserActions data={data} {...props} setRefreshTable={setRefreshTable} />;
  }

  return <InviteActions data={data} {...props} />;
};

UserTableActions.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object]),
  setToast: PropTypes.func.isRequired,
  setRefreshTable: PropTypes.func,
};

UserActions.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object]),
  setToast: PropTypes.func.isRequired,
  api: PropTypes.oneOfType([PropTypes.object]),
  node: PropTypes.oneOfType([PropTypes.object]),
  setRefreshTable: PropTypes.func,
};

InviteActions.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object]),
  setToast: PropTypes.func.isRequired,
  api: PropTypes.oneOfType([PropTypes.object]),
  node: PropTypes.oneOfType([PropTypes.object]),
};

export default UserTableActions;
